import { Box, Button, Card, Column, Table } from '@palmetto/palmetto-components';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

import { ActionOnAllocationAccountsResponse, IncentiveAssignmentStatus, incentiveStatusLabels } from 'types';

import { useGetIncentiveQuery } from '../../services/incentives';
import { MainContainer } from '../MainContainer';
import PageHeader from '../PageHeader';
interface ActionAccountsResultProps {
  result: ActionOnAllocationAccountsResponse;
  action: IncentiveAssignmentStatus;
  onAllocateMore: () => void;
  onBack: () => void;
  incentiveKey: string;
}

export const ActionAccountsResult = ({
  result,
  action,
  onAllocateMore,
  onBack,
  incentiveKey,
}: ActionAccountsResultProps) => {
  const { data: incentive } = useGetIncentiveQuery({ key: incentiveKey });

  const totalAttempted = [result.actionedAccounts, result.invalidAccounts, result.accountsAlreadyActioned].reduce(
    (sum, arr) => sum + arr.length,
    0,
  );

  const successColumns: Column[] = [
    {
      heading: 'Account ID',
      render: (_cell: any, row: any) => (
        <Link to={`/accounts/${row.accountId}`} style={{ textDecoration: 'none' }}>
          <Box as="span" color="primary">
            {row.accountId}
          </Box>
        </Link>
      ),
    },
  ];

  const alreadyAssignedColumns: Column[] = [
    {
      heading: 'Account ID',
      render: (_cell: any, row: any) => (
        <Link to={`/accounts/${row.accountId}`} style={{ textDecoration: 'none' }}>
          <Box as="span" color="primary">
            {row.accountId}
          </Box>
        </Link>
      ),
    },
    {
      heading: 'Current Allocation',
      render: (_cell: any, row: any) => row.allocationName,
    },
  ];

  const failedColumns: Column[] = [
    {
      heading: 'Account ID',
      render: (_cell: any, row: any) => row.accountId,
    },
    {
      heading: 'Reason',
      render: (_cell: any, row: any) => row.reason,
    },
  ];

  const successRows = result.actionedAccounts.map((accountId) => ({ accountId, id: accountId }));
  const alreadyAssignedRows = result.accountsAlreadyActioned.map((item) => ({ ...item, id: item.accountId }));
  const failedRows = result.invalidAccounts.map((item) => ({ ...item, id: item.accountId }));
  const actionTaken = IncentiveAssignmentStatus[action] || 'no supported action';
  const titleAction = incentiveStatusLabels[action] || 'Not Supported';
  return (
    <>
      <Helmet>
        <title>
          {titleAction} Results - {incentive?.name ?? 'Incentive'}
        </title>
      </Helmet>
      <MainContainer>
        <PageHeader
          eyebrow={<Link to={`/admin/incentives/${incentiveKey}`}>{incentive?.name || 'Incentive'}</Link>}
          title={`${titleAction} Results`}
          description={`${result.actionedAccounts.length} of ${totalAttempted} accounts were successfully ${actionTaken}.`}
        />

        <Box padding="lg 0" direction="column" childGap="lg" maxWidth="800px">
          {result.actionedAccounts.length > 0 && (
            <Card padding="lg">
              <Box direction="row" alignItems="center" justifyContent="space-between" margin="0 0 md 0">
                <Box as="h3" fontWeight="medium" fontSize="md" color="success">
                  Successfully {actionTaken} ({result.actionedAccounts.length})
                </Box>
              </Box>
              <Table rowKey="id" columns={successColumns} rows={successRows} isScrollable={{ x: true }} />
            </Card>
          )}

          {result.accountsAlreadyActioned.length > 0 && (
            <Card padding="lg">
              <Box direction="row" alignItems="center" justifyContent="space-between" margin="0 0 md 0">
                <Box as="h3" fontWeight="medium" fontSize="md" color="info">
                  Already {action === IncentiveAssignmentStatus.unallocated || actionTaken} (
                  {result.accountsAlreadyActioned.length})
                </Box>
              </Box>
              <Box fontSize="sm" color="body-secondary" margin="0 0 md 0">
                These accounts are already {action === IncentiveAssignmentStatus.unallocated || actionTaken} to an
                allocation for this incentive.
              </Box>
              <Table
                rowKey="id"
                columns={alreadyAssignedColumns}
                rows={alreadyAssignedRows}
                isScrollable={{ x: true }}
              />
            </Card>
          )}

          {result.invalidAccounts.length > 0 && (
            <Card padding="lg">
              <Box direction="row" alignItems="center" justifyContent="space-between" margin="0 0 md 0">
                <Box as="h3" fontWeight="medium" fontSize="md" color="danger">
                  Failed ({result.invalidAccounts.length})
                </Box>
              </Box>
              <Table rowKey="id" columns={failedColumns} rows={failedRows} isScrollable={{ x: true }} />
            </Card>
          )}

          <Box direction="row" childGap="md" justifyContent="flex-end">
            <Button variant="secondary" tone="neutral" size="md" onClick={onBack}>
              Back to Incentive
            </Button>
            <Button variant="primary" size="md" onClick={onAllocateMore}>
              {titleAction} More Accounts
            </Button>
          </Box>
        </Box>
      </MainContainer>
    </>
  );
};
